import { AxiosResponse } from 'axios';

import {
    ContractPriceListRequestMessage,
    ContractPriceListResponseMessage,
    ContractMessage,
    ContractState,
    EffectiveDateMode,
    PriceListActivationMode,
} from '../../../../types/ContractMessage';
import { PageMessage } from '../../../../types/PageMessage';
import { PaymentTermReference } from '../../../../types/PaymentTermReference';
import { ValidationResponse } from '../../../../types/ValidationResponse';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';
import { authorizedGatewayAxiosInstance } from '../instances/AuthorizedGatewayAxiosInstance';
import { nonEmpty } from '../paramUtils';

export interface GetContractsParams {
    page: number;
    size?: number;
    customerGroupIds?: string[];
    customerIds?: string[];
    supplierIds?: string[];
    states?: ContractState[];
    validUntilFrom?: string;
    validUntilTo?: string;
    evergreen?: boolean;
    searchQuery?: string;
    vesselIds?: string[];
}

export const getContracts = async (params: GetContractsParams): Promise<PageMessage<ContractMessage>> => {
    const response = await authorizedGatewayAxiosInstance.get('/v1/contracts', {
        params: {
            page: params.page,
            size: params.size,
            customerGroupIds: nonEmpty(params.customerGroupIds),
            customerIds: nonEmpty(params.customerIds),
            supplierIds: nonEmpty(params.supplierIds),
            states: nonEmpty(params.states),
            validUntilFrom: nonEmpty(params.validUntilFrom),
            validUntilTo: nonEmpty(params.validUntilTo),
            evergreen: params.evergreen,
            searchQuery: params.searchQuery?.trim() || undefined,
            vesselIds: nonEmpty(params.vesselIds),
        },
    });
    return response.data;
};

export const getContract = async (id: string): Promise<ContractMessage> => {
    const response = await authorizedGatewayAxiosInstance.get(`/v1/contracts/${id}`);
    return response.data;
};

export const downloadContractPriceList = (contractId: string, priceListId: string): Promise<AxiosResponse<Blob>> => {
    return authorizedAxiosInstance.get(`/v1/contracts/${contractId}/price-lists/${priceListId}/download`, {
        responseType: 'blob',
    });
};

export const downloadNormalizedPriceList = (contractId: string, priceListId: string): Promise<AxiosResponse<Blob>> => {
    return authorizedAxiosInstance.get(`/v1/contracts/${contractId}/price-lists/${priceListId}/download-normalized`, {
        responseType: 'blob',
    });
};

interface UpdateNormalizedFileParams {
    contractId: string;
    priceListId: string;
    normalizedFileId: string;
}

export const updateNormalizedFile = async ({
    contractId,
    priceListId,
    normalizedFileId,
}: UpdateNormalizedFileParams): Promise<ContractPriceListResponseMessage> => {
    const response = await authorizedAxiosInstance.patch(
        `/v1/contracts/${contractId}/price-lists/${priceListId}/normalize`,
        { normalizedFileId }
    );
    return response.data;
};

interface UpdateContractPriceListParams {
    contractId: string;
    priceListId: string;
    priceList: ContractPriceListRequestMessage;
}

export const updateContractPriceList = async ({
    contractId,
    priceListId,
    priceList,
}: UpdateContractPriceListParams): Promise<ContractPriceListResponseMessage> => {
    const response = await authorizedAxiosInstance.put(
        `/v1/contracts/${contractId}/price-lists/${priceListId}`,
        priceList
    );
    return response.data;
};

interface DeleteContractPriceListParams {
    contractId: string;
    priceListId: string;
}

export const deleteContractPriceList = async ({
    contractId,
    priceListId,
}: DeleteContractPriceListParams): Promise<void> => {
    await authorizedAxiosInstance.delete(`/v1/contracts/${contractId}/price-lists/${priceListId}`);
};

interface ChangeContractPriceListStateParams {
    contractId: string;
    priceListId: string;
}

export const activateContractPriceList = async ({
    contractId,
    priceListId,
}: ChangeContractPriceListStateParams): Promise<ContractPriceListResponseMessage> => {
    const response = await authorizedAxiosInstance.patch(
        `/v1/contracts/${contractId}/price-lists/${priceListId}/activate`
    );
    return response.data;
};

export const deactivateContractPriceList = async ({
    contractId,
    priceListId,
}: ChangeContractPriceListStateParams): Promise<ContractPriceListResponseMessage> => {
    const response = await authorizedAxiosInstance.patch(
        `/v1/contracts/${contractId}/price-lists/${priceListId}/deactivate`
    );
    return response.data;
};

export interface CreateContractMessage {
    name: string;
    customerGroupId?: string;
    supplierId?: string;
    customerIds?: string[];
    excludedVesselIds?: string[];
    state?: ContractState;
    contractReference?: string;
    description?: string;
    validFrom?: string;
    validUntil?: string;
    evergreen?: boolean;
    minimumVolume?: number;
    paymentTermReference?: PaymentTermReference;
    paymentTermReferenceDays?: number;
    gtc?: string;
    effectiveDateMode?: EffectiveDateMode;
    priceListActivationMode?: PriceListActivationMode;
}

export const createContract = async (data: CreateContractMessage): Promise<ContractMessage> => {
    const response = await authorizedAxiosInstance.post('/v1/contracts', data);
    return response.data;
};

interface UpdateContractMessage {
    id: string;
    data: CreateContractMessage;
}

export const updateContract = async ({ id, data }: UpdateContractMessage): Promise<ContractMessage> => {
    const response = await authorizedAxiosInstance.put(`/v1/contracts/${id}`, data);
    return response.data;
};

export const validateContract = async (
    data: CreateContractMessage,
    contractId?: string
): Promise<ValidationResponse> => {
    const response = await authorizedAxiosInstance.post('/v1/contracts/validation', data, {
        params: { contractId: contractId || undefined },
    });
    return response.data;
};

export const deleteContract = async (id: string): Promise<void> => {
    await authorizedAxiosInstance.delete(`/v1/contracts/${id}`);
};

export const awardContract = async (id: string): Promise<ContractMessage> => {
    const response = await authorizedAxiosInstance.patch(`/v1/contracts/${id}/award`);
    return response.data;
};

export const activateContract = async (id: string): Promise<ContractMessage> => {
    const response = await authorizedAxiosInstance.patch(`/v1/contracts/${id}/activate`);
    return response.data;
};

export const deactivateContract = async (id: string): Promise<ContractMessage> => {
    const response = await authorizedAxiosInstance.patch(`/v1/contracts/${id}/deactivate`);
    return response.data;
};

export const convertContractToDraft = async (id: string): Promise<ContractMessage> => {
    const response = await authorizedAxiosInstance.patch(`/v1/contracts/${id}/convert-to-draft`);
    return response.data;
};

export const getContractPriceLists = async (contractId: string): Promise<ContractPriceListResponseMessage[]> => {
    const response = await authorizedAxiosInstance.get(`/v1/contracts/${contractId}/price-lists`);
    return response.data.priceLists;
};

export const validatePriceLists = async (
    priceLists: ContractPriceListRequestMessage[],
    contractId?: string
): Promise<ValidationResponse> => {
    const response = await authorizedAxiosInstance.post(
        '/v1/price-lists/validation',
        { priceLists },
        { params: { contractId: contractId || undefined } }
    );
    return response.data;
};

export const createContractPriceLists = async ({
    contractId,
    priceLists,
}: {
    contractId: string;
    priceLists: ContractPriceListRequestMessage[];
}): Promise<ContractPriceListResponseMessage[]> => {
    const response = await authorizedAxiosInstance.post(`/v1/contracts/${contractId}/price-lists`, { priceLists });
    return response.data.priceLists;
};
